import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { MoneyInput } from '@/modules/common/components/MoneyInput';
import {
  integerToMoney,
  moneyToInteger,
} from '@/modules/common/lib/moneyHandler';
import { IAmount, ITeamOverrideWithTeam } from '@/types/learning-budget';
import { yupResolver } from '@hookform/resolvers/yup';
import { InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

type EditTeamLearningBudgetModalProps = Omit<
  ModalProps,
  'onClose' | 'title' | 'subTitle'
> & {
  onClose: (data?: ITeamOverrideWithTeam) => void;
  teamOverride: ITeamOverrideWithTeam | undefined;
};

const schema = yup.object().shape({
  budget: yup.object().shape({
    amount: yup
      .number()
      .typeError('Amount is required')
      .required('Amount is required')
      .positive('Amount must be positive'),
    currency: yup.string().required().oneOf(['EUR']),
  }),
});

export function EditTeamLearningBudgetModal({
  open,
  onClose,
  teamOverride,
  ...props
}: EditTeamLearningBudgetModalProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ budget: IAmount }>({
    values: {
      budget: teamOverride?.budget ?? {
        amount: teamOverride?.budget?.amount ?? 0,
        currency: teamOverride?.budget?.currency ?? 'EUR',
      },
    },
    resolver: yupResolver(schema),
  });

  const handleCancel = () => {
    onClose();
    reset();
  };

  const handleSave = (data: { budget: IAmount }) => {
    if (!teamOverride) {
      return;
    }

    onClose({
      ...teamOverride,
      budget: {
        amount: data.budget.amount,
        currency: data.budget.currency,
      },
    });
    reset();
  };

  return (
    <Modal
      {...props}
      open={open}
      onClose={handleCancel}
      title={`Edit Learning Budget for team ${teamOverride?.team.name}`}
      subTitle="Adjust the budget allocation for this team."
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="edit-team-learning-budget-form"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Button>
        </>
      }
      classes={{ paper: 'max-w-md' }}
    >
      <form
        id="edit-team-learning-budget-form"
        onSubmit={handleSubmit(handleSave)}
      >
        <Stack gap={3}>
          <FormControl className="flex flex-col gap-1">
            <Typography
              variant="subtitle2"
              component="label"
              htmlFor="budget-amount"
            >
              Learning Budget
            </Typography>
            <Controller
              name="budget.amount"
              control={control}
              render={({
                field: { value, onChange, ref: inputRef, ...field },
              }) => (
                <MoneyInput
                  {...field}
                  value={integerToMoney(value)}
                  onChange={e => {
                    onChange(moneyToInteger(e.target.value));
                  }}
                  inputRef={inputRef}
                  id="learning-budget-amount"
                  decimalScale={2}
                  startAdornment={
                    <Typography
                      className={`text-2xl font-semibold ${
                        isSubmitting ? 'text-gray-400' : ''
                      }`}
                    >
                      €
                    </Typography>
                  }
                  allowNegative={true}
                  classes={{
                    root: 'flex-1 rounded-xl text-center',
                    input: 'text-center text-2xl font-semibold',
                    focused: 'outline-none',
                  }}
                />
              )}
            />
            {errors.budget?.amount && (
              <FormHelperText error>
                {errors.budget.amount.message}
              </FormHelperText>
            )}
          </FormControl>

          <Alert
            severity="info"
            variant="filled"
            className="bg-info-light text-info-dark rounded-lg"
            icon={<InfoOutlined />}
          >
            This budget will be applied to all team members unless they have an
            individual override.
          </Alert>
        </Stack>
      </form>
    </Modal>
  );
}
